import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Response } from '@angular/http';
import { TranslateService } from '@ngx-translate/core';
import { ClientModel } from '../../shared/models/client.model';
import { OauthService } from '../../shared/services/oauth.service';
import { AccountFilesService } from '../../shared/services/account-files.service';

/**
 * Files the client sends at the account area (documents, photos, PDFs).
 *
 * Renders NOTHING unless the store gate (`account-file-upload-enabled` at
 * storeConfigurations) is on AND the API confirms `enabled` AND the merchant
 * published at least one file field — a store without any of that stays
 * byte-for-byte identical to today. Any API error also renders nothing: this
 * block never breaks the profile page.
 *
 * The client-side check of extension/size only spares a pointless round trip;
 * the backend is the one that decides (it also checks the real content).
 */
@Component({
    selector: 'account-files',
    templateUrl: './account.files.component.html',
    styleUrls: ['./../account.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AccountFilesComponent implements OnInit {
    public show = false;
    public loading = false;
    public fields: Array<any> = [];
    public allowedExtensions: Array<string> = [];
    public maxSizeKb = 0;

    private gateOn = false;
    private clientId = 0;

    constructor(
        protected oauth: OauthService,
        protected files: AccountFilesService,
        protected translate: TranslateService,
        protected activatedRoute: ActivatedRoute
    ) {
        this.oauth.loadedAuthenticatedUser$.subscribe((client: ClientModel) => {
            this.load(client);
        });
    }

    ngOnInit() {
        const data = this.activatedRoute.snapshot.data;
        const configurations = data['storeConfigurations'] || {};
        this.gateOn = !!(+configurations['account-file-upload-enabled'] || false);
    }

    /** Human readable limit, used at the hint line. */
    public get maxSizeMb(): number {
        return Math.round((this.maxSizeKb / 1024) * 10) / 10;
    }

    public extensionsLabel(): string {
        return this.allowedExtensions.join(', ');
    }

    public selected(field, event): void {
        const input = event.target;
        const file = input.files && input.files.length ? input.files[0] : null;

        // Clears the input so picking the very same file again fires 'change'.
        input.value = '';

        if (!file) {
            return;
        }

        field.confirmingRemoval = false;

        const rejection = this.reject(file);
        if (rejection !== null) {
            field.error = rejection;
            return;
        }

        this.upload(field, file);
    }

    public confirmRemoval(field): void {
        field.confirmingRemoval = true;
    }

    public cancelRemoval(field): void {
        field.confirmingRemoval = false;
    }

    public remove(field): void {
        field.confirmingRemoval = false;
        field.working = true;
        field.error = null;

        this.files.remove(this.clientId, +field.extra_field_id).subscribe(
            () => {
                field.working = false;
                field.file = null;
            },
            (response) => {
                field.working = false;
                field.error = this.errorMessage(response, 'ACCOUNT.FILES.REMOVE_ERROR');
            }
        );
    }

    private upload(field, file: File): void {
        field.working = true;
        field.error = null;

        this.files.upload(this.clientId, +field.extra_field_id, file).subscribe(
            (response: Response) => {
                field.working = false;
                field.file = response.json().data['file'];
            },
            (response) => {
                field.working = false;
                field.error = this.errorMessage(response, 'ACCOUNT.FILES.UPLOAD_ERROR');
            }
        );
    }

    private load(client: ClientModel): void {
        if (!this.gateOn || !client || !client.id) {
            return;
        }

        this.clientId = +client.id;
        this.loading = true;

        this.files.list(this.clientId).subscribe(
            (response: Response) => {
                const data = response.json().data;
                this.loading = false;

                if (!data['enabled']) {
                    return; // backend says off: render nothing
                }

                const settings = data['settings'] || {};
                this.allowedExtensions = settings['allowed_extensions'] || [];
                this.maxSizeKb = +settings['max_size_kb'] || 0;
                this.fields = (data['fields'] || []).map((field) => {
                    field.working = false;
                    field.error = null;
                    field.confirmingRemoval = false;
                    return field;
                });

                // Nothing published: nothing to show.
                this.show = this.fields.length > 0;
            },
            () => {
                this.loading = false; // any API error: render nothing
            }
        );
    }

    /** Returns the reason to refuse the file, or null when it looks fine. */
    private reject(file: File): string {
        const extension = (file.name.split('.').pop() || '').toLowerCase();

        if (
            this.allowedExtensions.length
            && this.allowedExtensions.indexOf(extension) === -1
        ) {
            return this.translate.instant(
                'ACCOUNT.FILES.INVALID_EXTENSION',
                { extensions: this.extensionsLabel() }
            );
        }

        if (this.maxSizeKb > 0 && file.size > this.maxSizeKb * 1024) {
            return this.translate.instant(
                'ACCOUNT.FILES.TOO_BIG',
                { size: this.maxSizeMb }
            );
        }

        return null;
    }

    /** Backend message when there is one, generic translation otherwise. */
    private errorMessage(response, fallbackKey: string): string {
        try {
            const body = response.json();
            if (body && body.message) {
                return body.message;
            }
        } catch (e) {
            // response without a JSON body: falls back to the generic message
        }

        return this.translate.instant(fallbackKey);
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src3894461559/src/src/app/account/files/account.files.component.ts